import React, { useCallback, useState, useRef, useEffect } from "react";
import { Formik } from "formik";
import { Button, Form, ProgressBar } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useSelector, useDispatch } from "react-redux";
import { fetchPropertyByRealId } from "../../state/actions/propertyActions";
import {
  addImages,
  checkImageProcessingStatus,
} from "../../state/actions/agentActions";
import FileInput from "../../components/Form/FileInput";
import styles from "./styles.module.sass";
import { Link, useParams, useNavigate } from "react-router-dom";
import ImageContainer from "../../components/Image/ImageContainer";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAddImages } from "../../hooks/useAddImages";
import { toast } from "react-toastify";

const AddImages = () => {
  const dispatch = useDispatch();
  const property = useSelector((state) => state.property.currentProperty);
  const { id: propertyId } = useParams();
  const navigate = useNavigate();

  const {
    showLoader,
    setShowLoader,
    pageLoading,
    setPageLoading,
    toggleSelectAll,
    isAllSelected,
    isAllDeselected,
  } = useAddImages({ propertyId, dispatch });

  const [uploadProgress, setUploadProgress] = useState(null);
  const pollingRef = useRef(null);
  const totalImagesRef = useRef(0);
  const existingImagesRef = useRef(0);
  const lastProgressRef = useRef(0);
  const staleSinceRef = useRef(null);
  const containerRef = useRef(null);
  const STALE_TIMEOUT_MS = 60000;

  const checkProgress = useCallback(
    async (propId) => {
      try {
        const status = await dispatch(
          checkImageProcessingStatus(
            propId,
            totalImagesRef.current,
            existingImagesRef.current
          )
        );
        const { total, processed, processing_completed } = status;

        if (processed > lastProgressRef.current) {
          lastProgressRef.current = processed;
          staleSinceRef.current = null;
        } else {
          if (!staleSinceRef.current) staleSinceRef.current = Date.now();
          if (Date.now() - staleSinceRef.current >= STALE_TIMEOUT_MS) {
            totalImagesRef.current = 0;
            lastProgressRef.current = 0;
            staleSinceRef.current = null;
            setUploadProgress(null);
            setShowLoader(false);
            toast.warning(
              "Obrada slika traje duže nego očekivano. Osvežite stranicu da vidite rezultat."
            );
            await dispatch(fetchPropertyByRealId(propId));
            return;
          }
        }

        setUploadProgress({
          total,
          processed,
          percentage: total > 0 ? Math.round((processed / total) * 100) : 0,
          phase: "processing",
        });

        if (!processing_completed) {
          pollingRef.current = setTimeout(() => checkProgress(propId), 2000);
        } else {
          totalImagesRef.current = 0;
          lastProgressRef.current = 0;
          staleSinceRef.current = null;
          setUploadProgress(null);
          setShowLoader(false);
          toast.success("Uspešno dodate slike");
          await dispatch(fetchPropertyByRealId(propId));
        }
      } catch (error) {
        console.error("Error checking progress:", error);
        totalImagesRef.current = 0;
        setUploadProgress(null);
        setShowLoader(false);
      }
    },
    [dispatch, setShowLoader]
  );

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  const isUploading = showLoader || !!uploadProgress;

  useEffect(() => {
    dispatch({ type: "SET_UPLOADING_IMAGES", payload: isUploading });
    return () => {
      if (isUploading) {
        dispatch({ type: "SET_UPLOADING_IMAGES", payload: false });
      }
    };
  }, [isUploading, dispatch]);

  useEffect(() => {
    if (!isUploading) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    const handleNavigationClick = (e) => {
      const link = e.target.closest("a[href]");
      if (link) {
        const href = link.getAttribute("href");
        if (!href || href.startsWith("http") || href.startsWith("#")) return;
        if (link.target === "_blank") return;
      } else {
        const button = e.target.closest(
          'button, [role="button"], input[type="file"]',
        );
        if (!button) return;
        if (containerRef.current?.contains(button)) return;
      }

      e.preventDefault();
      e.stopPropagation();

      window.alert(
        "Slike se još uvek uploaduju ili obrađuju. Sačekajte da se završi pre nego što napustite stranicu.",
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleNavigationClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleNavigationClick, true);
    };
  }, [isUploading, navigate]);

  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      if (!values?.pictures) return;

      setShowLoader(true);
      totalImagesRef.current = values.pictures.length;
      existingImagesRef.current = property.pictures?.length || 0;
      lastProgressRef.current = 0;
      staleSinceRef.current = null;
      setUploadProgress({
        total: values.pictures.length,
        processed: 0,
        percentage: 0,
        phase: "upload",
      });

      const onProgress = ({ uploaded, total, percentage }) => {
        setUploadProgress({
          total,
          processed: uploaded,
          percentage,
          phase: "upload",
        });
      };

      try {
        await dispatch(
          addImages(property.id, values.pictures, propertyId, onProgress)
        );
        resetForm();
        // Upload završen, pokreni polling za BE obradu
        checkProgress(propertyId);
      } catch (error) {
        totalImagesRef.current = 0;
        setUploadProgress(null);
        setShowLoader(false);
      }
    },
    [dispatch, property.id, propertyId, setShowLoader, checkProgress]
  );
  if (pageLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Link to={`/properties/${propertyId}/`}>Povratak na nekretninu</Link>

      <Formik onSubmit={handleSubmit} initialValues={{}}>
        {({
          handleSubmit: formikSubmit,
          setFieldValue,
          values,
          touched,
          errors,
        }) => (
          <Form>
            <Row>
              <Col lg={4} className="mb-3">
                <FileInput
                  values={values}
                  onChange={(e) =>
                    setFieldValue("pictures", e.currentTarget.files)
                  }
                  label="Slike nekretnine"
                  formName="images"
                  accept="image/png, image/jpeg"
                  errors={errors}
                  touched={touched}
                  multiple
                  disabled={isUploading}
                />
              </Col>
            </Row>
            <Row>
              <Col lg={3}>
                <Button
                  className={styles.submitButton}
                  onClick={formikSubmit}
                  disabled={showLoader || !values.pictures}
                >
                  {showLoader ? (
                    <LoadingSpinner
                      width={20}
                      height={20}
                      fontSize={20}
                      componentLoader={true}
                    />
                  ) : (
                    "Dodaj Slike"
                  )}
                </Button>
              </Col>
            </Row>
            {uploadProgress && (
              <Row className="mt-3">
                <Col lg={6}>
                  <div className="mb-2">
                    {uploadProgress.phase === "upload"
                      ? `Dodavanje slika: ${uploadProgress.processed} / ${uploadProgress.total}`
                      : `Obrada slika: ${uploadProgress.processed} / ${uploadProgress.total}`}
                  </div>
                  <ProgressBar
                    now={uploadProgress.percentage}
                    label={`${uploadProgress.percentage}%`}
                    animated={uploadProgress.percentage < 100}
                  />
                </Col>
              </Row>
            )}
          </Form>
        )}
      </Formik>

      <Row>
        <Col>
          <Button
            className={styles.action}
            disabled={showLoader}
            onClick={toggleSelectAll}
          >
            {isAllSelected ? "Deselektuj Sve Slike" : "Izaberi Sve Slike"}
          </Button>
        </Col>
      </Row>

      {property.pictures && (
        <Row>
          <DndProvider backend={HTML5Backend}>
            <ImageContainer
              allSelected={isAllSelected}
              allDeselected={isAllDeselected}
              images={property.pictures}
              id={propertyId}
              _id={property.id}
              setPageLoading={setPageLoading}
              pageLoading={pageLoading}
              isUploading={isUploading}
            />
          </DndProvider>
        </Row>
      )}
    </div>
  );
};

export default AddImages;
